import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getBcmPhases, loadBcmPhase, submitBcmPhase } from '../services/bcmService';
import { removeEvidenceFile } from '../services/uploadService';
import { getPhaseProgress, getResponsesForPhase } from '../utils/bcmProgress';
import { getIncompleteQuestions, validateBeforeSubmit } from '../utils/bcmValidation';
import { useBcmAutoSave } from './useBcmAutoSave';
import { useUploadManager } from './useUploadManager';

const MAX_FILE_SIZE = 150 * 1024 * 1024;

export const useBcmAssessment = (navigate) => {
  const [phases, setPhases] = useState([]);
  const [responses, setResponses] = useState([]);
  const [assessmentIds, setAssessmentIds] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState({});
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [token] = useState(localStorage.getItem('access_token'));

  const [saveState, setSaveState] = useState({
    hasUnsyncedChanges: false,
    blockedReason: null,
    lastError: null,
  });

  const selectedPhase = useMemo(
    () => phases.find((p) => p.id === selectedPhaseId),
    [phases, selectedPhaseId]
  );

  const getSelectedPhase = useCallback(
    () => phases.find((p) => p.id === selectedPhaseId),
    [phases, selectedPhaseId]
  );

  const markSavedTime = useCallback(() => setLastSaved(new Date().toLocaleTimeString()), []);

  const onResponseIdSaved = useCallback((questionId, id) => {
    setResponses((prev) => prev.map((r) => (r.question === questionId ? { ...r, id } : r)));
  }, []);

  const {
    saveSingleResponse,
    enqueueSave,
    debouncedSave,
    isBlockedByAuth,
    isOffline,
  } = useBcmAutoSave({
    getSelectedPhase,
    assessmentIds,
    submissionStatus,
    onResponseIdSaved,
    onSaved: markSavedTime,
    onSaveStateChange: setSaveState,
  });

  const ensureResponse = useCallback(
    async (questionId) => {
      const existing = responses.find((r) => r.question === questionId);
      if (!existing) return null;
      if (existing.id) return existing.id;
      return saveSingleResponse(existing);
    },
    [responses, saveSingleResponse]
  );

  const onUploadSuccess = useCallback(
    (questionId, uploadedFile) => {
      setResponses((prev) =>
        prev.map((r) =>
          r.question === questionId
            ? { ...r, backendFiles: [...(r.backendFiles || []), uploadedFile] }
            : r
        )
      );
      markSavedTime();
      setSaveState((prev) => ({
        ...prev,
        hasUnsyncedChanges: false,
        blockedReason: null,
        lastError: null,
      }));
    },
    [markSavedTime]
  );

  const uploadManager = useUploadManager({
    responseType: 'bcm',
    concurrency: 3,
    ensureResponse,
    onUploadSuccess,
  });

  useEffect(() => {
    if (isBlockedByAuth || isOffline) uploadManager.pauseAll();
    else if (uploadManager.isPaused) uploadManager.resumeAll();
  }, [isBlockedByAuth, isOffline, uploadManager]);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        const phasesRes = await getBcmPhases();

        const phasesData = (phasesRes.data || []).sort((a, b) => {
          const aMatch = a.code?.match(/\d+/);
          const bMatch = b.code?.match(/\d+/);
          if (aMatch && bMatch) return parseInt(aMatch[0], 10) - parseInt(bMatch[0], 10);
          return a.id - b.id;
        });

        setPhases(phasesData);

        const idsMap = {};
        const statusMap = {};
        const allResponses = [];

        for (const phase of phasesData) {
          try {
            const phaseRes = await loadBcmPhase(phase.id);
            idsMap[phase.id] = phaseRes.data.assessment_id;
            statusMap[phase.id] = phaseRes.data.submission_status;

            phaseRes.data.questions.forEach((q) => {
              const backendFiles = Array.isArray(q.response?.evidence_files)
                ? q.response.evidence_files
                    .filter((f) => f && f.id)
                    .map((f) => ({
                      id: f.id,
                      name: f.file_name || f.name || 'Unknown File',
                      file_url: f.file_url || f.url,
                      uploaded_at: f.uploaded_at || new Date().toISOString(),
                    }))
                : [];

              allResponses.push({
                id: q.response?.id || null,
                question: q.id,
                answer:
                  q.response?.performed === 'yes'
                    ? 'Yes'
                    : q.response?.performed === 'no'
                    ? 'No'
                    : '',
                evidence: q.response?.evidence_text || '',
                backendFiles,
              });
            });
          } catch {
            statusMap[phase.id] = 'draft';
          }
        }

        setAssessmentIds(idsMap);
        setSubmissionStatus(statusMap);
        setResponses(allResponses);
        if (phasesData.length > 0) setSelectedPhaseId(phasesData[0].id);
      } catch (e) {
        console.error(e);
        setError('Failed to load BCM assessment data.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [navigate, token]);

  const handleResponse = useCallback(
    (questionId, answer) => {
      const phase = getSelectedPhase();
      if (submissionStatus[phase?.id] === 'submitted') {
        toast.error('This phase has been submitted and locked.');
        return;
      }

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const base = responses.find((r) => r.question === questionId) || {
        id: null,
        question: questionId,
        answer: '',
        evidence: '',
        backendFiles: [],
      };
      const next = { ...base, answer };

      setResponses((prev) =>
        prev.some((r) => r.question === questionId)
          ? prev.map((r) => (r.question === questionId ? { ...r, answer } : r))
          : [...prev, next]
      );

      debouncedSave(next);
    },
    [getSelectedPhase, submissionStatus, responses, debouncedSave]
  );

  const handleEvidence = useCallback(
    (questionId, evidence) => {
      const phase = getSelectedPhase();
      if (submissionStatus[phase?.id] === 'submitted') {
        toast.error('This phase has been submitted and locked.');
        return;
      }

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const base = responses.find((r) => r.question === questionId) || {
        id: null,
        question: questionId,
        answer: '',
        evidence: '',
        backendFiles: [],
      };
      const next = { ...base, evidence };

      setResponses((prev) =>
        prev.some((r) => r.question === questionId)
          ? prev.map((r) => (r.question === questionId ? { ...r, evidence } : r))
          : [...prev, next]
      );

      debouncedSave(next);
    },
    [getSelectedPhase, submissionStatus, responses, debouncedSave]
  );

  const handleFilesSelected = useCallback(
    (questionId, fileList) => {
      const phase = getSelectedPhase();
      if (submissionStatus[phase?.id] === 'submitted') {
        toast.error('This phase has been submitted and locked.');
        return;
      }

      const files = Array.from(fileList || []);
      if (!files.length) return;

      setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));

      const latest = responses.find((r) => r.question === questionId);
      if (latest) enqueueSave(latest);

      const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
      oversized.forEach((f) => toast.error(`✗ ${f.name} exceeds the maximum size of 150 MB.`));

      const allowedFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
      if (!allowedFiles.length) return;

      const result = uploadManager.addFiles({ questionId, files: allowedFiles });

      result.acceptedFiles?.forEach((f) => toast.success(`✓ ${f.name} — Queued`));
      result.rejectedFiles?.forEach((f) => toast.error(`✗ ${f.reason}`));
    },
    [getSelectedPhase, submissionStatus, responses, enqueueSave, uploadManager]
  );

  const retryPendingFile = useCallback(
    (questionId, taskId) => {
      const task = uploadManager.tasks.find((t) => t.id === taskId && t.questionId === questionId);
      if (task) uploadManager.retryUpload(task.id);
    },
    [uploadManager]
  );

  const removePendingFile = useCallback(
    (questionId, taskId) => {
      const task = uploadManager.tasks.find((t) => t.id === taskId && t.questionId === questionId);
      if (task) uploadManager.removeTask(task.id);
    },
    [uploadManager]
  );

  const handleRemoveBackendFile = useCallback(
    async (questionId, fileId) => {
      const phase = getSelectedPhase();
      if (submissionStatus[phase?.id] === 'submitted') {
        toast.error('Cannot modify submitted responses');
        return;
      }

      const response = responses.find((r) => r.question === questionId);
      if (!response?.id) {
        toast.error('Response not found');
        return;
      }

      try {
        await removeEvidenceFile({ responseId: response.id, responseType: 'bcm', fileId });

        setResponses((prev) =>
          prev.map((r) =>
            r.question === questionId
              ? { ...r, backendFiles: (r.backendFiles || []).filter((f) => f.id !== fileId) }
              : r
          )
        );
        setSaveState((prev) => ({ ...prev, hasUnsyncedChanges: true }));
        toast.success('File removed successfully');
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || 'Failed to remove file');
      }
    },
    [getSelectedPhase, submissionStatus, responses]
  );

  const responsesWithUploadState = useMemo(() => {
    return responses.map((r) => {
      const uploads = uploadManager
        .getTasksByQuestion(r.question)
        .filter((t) => t.status !== 'success')
        .map((t) => ({
          tempId: t.id,
          name: t.name,
          size: t.size,
          status: t.status === 'canceled' ? 'failed' : t.status,
          error: t.error || '',
          progress: t.progress || 0,
        }));

      return { ...r, newFiles: uploads };
    });
  }, [responses, uploadManager]);

  const progress = useMemo(
    () => getPhaseProgress(selectedPhase, responsesWithUploadState),
    [selectedPhase, responsesWithUploadState]
  );

  const incompleteQuestions = useMemo(
    () => getIncompleteQuestions(selectedPhase, responsesWithUploadState),
    [selectedPhase, responsesWithUploadState]
  );

  const submissionState = useMemo(() => {
    if (!selectedPhase) return { canSubmit: false, reason: 'No phase selected.', pendingUploads: 0, failedUploads: 0 };
    if (submissionStatus[selectedPhase.id] === 'submitted') return { canSubmit: false, reason: 'Assessment already submitted.', pendingUploads: 0, failedUploads: 0 };
    if (isBlockedByAuth) return { canSubmit: false, reason: 'Session expired. Please sign in again.', pendingUploads: 0, failedUploads: 0 };
    if (isOffline) return { canSubmit: false, reason: 'You are offline. Reconnect before submitting.', pendingUploads: 0, failedUploads: 0 };

    const questionIds = selectedPhase.focus_areas?.flatMap((fa) => (fa.questions || []).map((q) => q.id)) || [];
    const phaseTasks = uploadManager.tasks.filter((t) => questionIds.includes(t.questionId));

    const queued = phaseTasks.filter((t) => t.status === 'queued').length;
    const uploading = phaseTasks.filter((t) => t.status === 'uploading').length;
    const failed = phaseTasks.filter((t) => t.status === 'failed').length;

    const hasPendingUploads = queued + uploading > 0;
    const hasIncomplete = incompleteQuestions.length > 0;

    let reason = '';
    if (hasIncomplete) reason = 'Complete all required questions/evidence.';
    else if (hasPendingUploads) reason = 'Wait for evidence uploads to finish.';
    else if (failed > 0) reason = 'Retry failed uploads before submitting.';
    else if (submitting) reason = 'Submitting...';
    else reason = 'Ready to submit.';

    return {
      canSubmit: !hasIncomplete && !hasPendingUploads && failed === 0 && !submitting,
      reason,
      pendingUploads: queued + uploading,
      failedUploads: failed,
    };
  }, [selectedPhase, submissionStatus, uploadManager.tasks, incompleteQuestions, submitting, isBlockedByAuth, isOffline]);

  const submitCurrentPhase = useCallback(async () => {
    const phase = getSelectedPhase();
    if (!phase) {
      toast.error('Phase not found.');
      return false;
    }

    if (submissionStatus[phase.id] === 'submitted') {
      toast.warning('This phase has already been submitted and locked.');
      return false;
    }

    if (!submissionState.canSubmit) {
      toast.warning(submissionState.reason || 'Not ready to submit.');
      return false;
    }

    const phaseResponses = getResponsesForPhase(phase, responsesWithUploadState);
    const validationError = validateBeforeSubmit(phaseResponses);
    if (validationError) {
      toast.warning(validationError);
      return false;
    }

    const payload = phaseResponses.map((r) => ({
      question_id: r.question,
      performed: r.answer.toLowerCase(),
      evidence_text: r.evidence,
    }));

    try {
      setSubmitting(true);
      await submitBcmPhase(phase.id, {
        assessment_id: assessmentIds[phase.id],
        responses: payload,
      });

      setSubmissionStatus((prev) => ({ ...prev, [phase.id]: 'submitted' }));
      setSaveState({ hasUnsyncedChanges: false, blockedReason: null, lastError: null });
      toast.success(`Phase "${phase.title}" submitted successfully!`);
      return true;
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Submission failed');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [getSelectedPhase, submissionStatus, submissionState, responsesWithUploadState, assessmentIds]);

  return {
    phases,
    responses: responsesWithUploadState,
    selectedPhase,
    selectedPhaseId,
    setSelectedPhaseId,
    submissionStatus,
    loading,
    error,
    submitting,
    lastSaved,
    progress,
    incompleteQuestions,
    submissionState,

    saveState,
    isBlockedByAuth,
    isOffline,

    handleResponse,
    handleEvidence,
    handleFilesSelected,
    retryPendingFile,
    removePendingFile,
    handleRemoveBackendFile,
    submitCurrentPhase,

    uploadManager,

    getPhaseProgress: (phaseId) => {
      const phase = phases.find((p) => p.id === phaseId);
      return getPhaseProgress(phase, responsesWithUploadState);
    },
  };
};